import type { ChatMapSize, UtilityMode, VisibilityFilter } from "../types";

/**
 * Current map viewport, published (debounced) by the map's BoundsReporter so the
 * list can narrow to "places in view". null = viewport-sync inactive (zoomed
 * out below the sync threshold) → the list shows the full curated browse.
 */
export interface ViewportBounds {
  readonly north: number;
  readonly south: number;
  readonly east: number;
  readonly west: number;
  readonly centerLat: number;
  readonly centerLng: number;
}

export interface DiscoverState {
  utilityMode: UtilityMode;
  activeTab: string;
  visibility: VisibilityFilter;
  searchQuery: string;
  selectedItemId: string | null;
  isDetailOpen: boolean;
  isMapMode: boolean;
  likedIds: ReadonlySet<string>;
  selectedCitySlug: string | null;
  /**
   * Activity slugs surfaced by the most recent AI Chat turn. When set
   * (and the user is on the chat tab) the map filter restricts to these
   * — every assistant reply re-focuses the map onto its own pins.
   * Empty array = no filter active.
   */
  aiMatchSlugs: readonly string[];
  /** Live map viewport for the "places in view" list; null when zoomed out. */
  viewportBounds: ViewportBounds | null;
  /**
   * How wide the map runs beside the AI Chat. Session state, not persisted:
   * a guest who widened the map to place one answer should not find the chat
   * squeezed the next time they open it.
   */
  chatMapSize: ChatMapSize;
  /** Selected restaurant sub-tags (rooftop, bar, …); OR-matched via matchesSubtags. Only shown/used when the restaurants bucket is active. */
  readonly selectedSubtags: readonly string[];
}

export type DiscoverAction =
  | { type: "SET_UTILITY_MODE"; mode: UtilityMode }
  | { type: "SET_TAB"; tab: string }
  | { type: "SET_VISIBILITY"; visibility: VisibilityFilter }
  | { type: "SET_SEARCH"; query: string }
  | { type: "SELECT_ITEM"; itemId: string }
  | { type: "CLOSE_DETAIL" }
  | { type: "TOGGLE_MAP_MODE" }
  | { type: "RESET_FILTERS" }
  | { type: "TOGGLE_LIKED"; itemId: string }
  | { type: "SET_LIKED_IDS"; ids: readonly string[] }
  | { type: "SET_CITY"; slug: string | null }
  | { type: "SET_AI_MATCH_SLUGS"; slugs: readonly string[] }
  | { type: "SET_VIEWPORT_BOUNDS"; bounds: ViewportBounds | null }
  | { type: "SET_SUBTAGS"; subtags: readonly string[] }
  | { type: "SET_CHAT_MAP_SIZE"; size: ChatMapSize };

export const DEFAULT_TAB = "for-you";

/**
 * @param utilityMode the section the URL asked for. Passed in rather than set
 *   from an effect afterwards: an effect runs after the first paint, so
 *   `/ai-chat` rendered Explore and then flipped to the chat about a second
 *   later — visibly, and in the server's HTML, which is what a crawler reads.
 */
export function createInitialDiscoverState(
  utilityMode: UtilityMode = "explore",
): DiscoverState {
  return {
    utilityMode,
    activeTab: DEFAULT_TAB,
    visibility: "all",
    searchQuery: "",
    selectedItemId: null,
    isDetailOpen: false,
    isMapMode: false,
    likedIds: new Set(),
    selectedCitySlug: null,
    aiMatchSlugs: [],
    viewportBounds: null,
    selectedSubtags: [],
    chatMapSize: "half",
  };
}

export function discoverReducer(
  state: DiscoverState,
  action: DiscoverAction,
): DiscoverState {
  switch (action.type) {
    case "SET_UTILITY_MODE":
      return { ...state, utilityMode: action.mode };
    case "SET_TAB":
      return { ...state, activeTab: action.tab };
    case "SET_VISIBILITY":
      return { ...state, visibility: action.visibility };
    case "SET_SEARCH":
      return { ...state, searchQuery: action.query };
    case "SELECT_ITEM":
      return { ...state, selectedItemId: action.itemId, isDetailOpen: true };
    case "CLOSE_DETAIL":
      return { ...state, isDetailOpen: false };
    case "TOGGLE_MAP_MODE":
      return { ...state, isMapMode: !state.isMapMode };
    case "RESET_FILTERS":
      return {
        ...state,
        activeTab: DEFAULT_TAB,
        visibility: "all",
        searchQuery: "",
        // Clearing filters also drops any "places in view" narrowing so the
        // list returns to the full curated browse instead of a stale viewport.
        viewportBounds: null,
        selectedSubtags: [],
      };
    case "TOGGLE_LIKED": {
      const next = new Set(state.likedIds);
      if (next.has(action.itemId)) next.delete(action.itemId);
      else next.add(action.itemId);
      return { ...state, likedIds: next };
    }
    case "SET_LIKED_IDS":
      return { ...state, likedIds: new Set(action.ids) };
    case "SET_CITY":
      // Drop the old viewport so switching cities doesn't briefly show the
      // previous city's "in view" results before the map's next idle fires.
      return { ...state, selectedCitySlug: action.slug, viewportBounds: null };
    case "SET_AI_MATCH_SLUGS":
      return { ...state, aiMatchSlugs: action.slugs };
    case "SET_VIEWPORT_BOUNDS":
      // No-op when already cleared — the map fires "below threshold" repeatedly
      // while zoomed out; this avoids needless re-renders of every consumer.
      if (state.viewportBounds === null && action.bounds === null) return state;
      return { ...state, viewportBounds: action.bounds };
    case "SET_SUBTAGS":
      return { ...state, selectedSubtags: action.subtags };
    case "SET_CHAT_MAP_SIZE":
      return { ...state, chatMapSize: action.size };
    default:
      return state;
  }
}
